import { BrandLogo } from "@/components/site/BrandLogo";

interface ErrorStateProps {
  title?: string;
  message?: string;
  detail?: string;
  onRetry?: () => void;
  retryLabel?: string;
  showHomeLink?: boolean;
}

/**
 * Branded full-screen error fallback used by the global router error boundary
 * and any route-level errorComponent. Mirrors the visual language of the 404.
 */
export function ErrorState({
  title = "Something went wrong",
  message = "An unexpected error occurred on our end. Please try again in a moment.",
  detail,
  onRetry,
  retryLabel = "Try again",
  showHomeLink = true,
}: ErrorStateProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="mb-8 flex justify-center">
          <BrandLogo size={28} />
        </div>
        <p className="text-[12px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Error 500
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{message}</p>
        {detail && import.meta.env.DEV && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left font-mono text-[11px] text-destructive">
            {detail}
          </pre>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-10 items-center justify-center rounded-[8px] bg-primary px-5 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {retryLabel}
            </button>
          )}
          {showHomeLink && (
            <a
              href="/"
              className="inline-flex h-10 items-center justify-center rounded-[8px] border border-border bg-background px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              Go home
            </a>
          )}
        </div>
        <p className="mt-8 text-[11.5px] text-muted-foreground">
          Still seeing this? Email{" "}
          <a href="mailto:support@perceptica.com" className="underline underline-offset-2">
            support@perceptica.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}